import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Stack,
  Checkbox,
  Alert,
  Textarea,
  Divider,
} from '@mui/joy';
import { insuranceApi, type InsurancePayoutItem, type InsuranceProvider } from '../../lib/insuranceApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  item: InsurancePayoutItem | null;
  onSave: () => void;
}

// Category display labels for known categories
const CATEGORY_LABELS: Record<string, string> = {
  'RFG': 'Roofing (RFG)',
  'PNT': 'Painting (PNT)',
  'LAB': 'Labor (LAB)',
  'FEE': 'Fees (FEE)',
  'SFG': 'Siding/Fascia/Gutters (SFG)',
  'MISC': 'Miscellaneous (MISC)',
  // Add more as needed
};

// Units of measure from backend (matching insurance formats)
const UNITS_OF_MEASURE = [
  { value: 'SQ', label: 'Square' },
  { value: 'SF', label: 'Square Foot' },
  { value: 'LF', label: 'Linear Foot' },
  { value: 'EA', label: 'Each' },
  { value: 'GAL', label: 'Gallon' },
  { value: 'BD', label: 'Bundle' },
  { value: 'BX', label: 'Box' },
  { value: 'CT', label: 'Carton' },
  { value: 'CS', label: 'Case' },
  { value: 'RL', label: 'Roll' },
  { value: 'PC', label: 'Piece' },
  { value: 'PK', label: 'Pack' },
  { value: 'LB', label: 'Pound' },
];

export default function InsurancePayoutItemForm({ open, onClose, item, onSave }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState<InsuranceProvider | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Form fields
  const [formData, setFormData] = useState({
    description: '',
    category: 'MISC',
    unit_of_measure: 'EA',
    replace_price: '',
    remove_price: '',
    detach_reset_price: '',
    supporting_info: '',
    is_active: true,
  });

  useEffect(() => {
    if (open) {
      // Load available categories for the organization
      loadCategories();
      
      if (item) {
        // Reset form data when opening with an item
        setFormData({
          description: item.description || '',
          category: item.category || 'MISC',
          unit_of_measure: item.unit_of_measure || 'EA',
          replace_price: item.replace_price || '',
          remove_price: item.remove_price || '',
          detach_reset_price: item.detach_reset_price || '',
          supporting_info: item.supporting_info || '',
          is_active: item.is_active !== undefined ? item.is_active : true,
        });
        
        // Load provider information
        loadProvider(item.provider);
      }
    }
  }, [open, item]);

  const loadCategories = async () => {
    try {
      const response = await insuranceApi.payoutItems.getCategories();
      setCategories(response);
      // If no categories loaded, set some defaults
      if (!response || response.length === 0) {
        setCategories(['RFG', 'PNT', 'MISC']);
      }
    } catch (err: any) {
      console.error('Failed to load categories:', err);
      // Fallback to default categories
      setCategories(['RFG', 'PNT', 'MISC']);
    }
  };

  const loadProvider = async (providerId: number) => {
    try {
      const response = await insuranceApi.providers.get(providerId);
      setProvider(response);
    } catch (err: any) {
      console.error('Failed to load provider:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!item) {
      setError('No item to update');
      return;
    }
    
    // Validation
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    
    // At least one price should be provided
    if (!formData.replace_price && !formData.remove_price && !formData.detach_reset_price) {
      setError('At least one price must be provided');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        replace_price: formData.replace_price ? formData.replace_price.toString() : null,
        remove_price: formData.remove_price ? formData.remove_price.toString() : null,
        detach_reset_price: formData.detach_reset_price ? formData.detach_reset_price.toString() : null,
        supporting_info: formData.supporting_info || null,
      };

      // Update existing item
      await insuranceApi.payoutItems.update(item.id, data);
      showSuccess('Insurance payout item updated successfully');
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to update item:', err);
      const errorMessage = err?.response?.data?.message || 
                         err?.response?.data?.error ||
                         err?.message ||
                         'Failed to update insurance payout item';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const formatPrice = (value: string) => {
    // Remove any non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600 }}>
        <ModalClose />
        <Typography level="h4">
          Edit Insurance Payout Item
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}
            
            {/* Read-only fields */}
            <Box sx={{ bgcolor: 'background.level1', p: 2, borderRadius: 'sm' }}>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Provider</FormLabel>
                  <Typography level="body-md">
                    {provider?.name} {provider?.version && `(v${provider.version})`}
                  </Typography>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Selector Code</FormLabel>
                  <Typography level="body-md" sx={{ fontFamily: 'monospace' }}>
                    {item?.selector_code}
                  </Typography>
                </FormControl>
              </Stack>
            </Box>

            <Divider />
            
            {/* Editable fields */}
            <FormControl required>
              <FormLabel>Description</FormLabel>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter item description"
              />
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl required>
                <FormLabel>Category</FormLabel>
                <Select
                  value={formData.category}
                  onChange={(_, value) => setFormData({ ...formData, category: value as string })}
                >
                  {categories.map(cat => (
                    <Option key={cat} value={cat}>
                      {CATEGORY_LABELS[cat] || cat}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required>
                <FormLabel>Unit of Measure</FormLabel>
                <Select
                  value={formData.unit_of_measure}
                  onChange={(_, value) => setFormData({ ...formData, unit_of_measure: value as string })}
                >
                  {UNITS_OF_MEASURE.map(unit => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Price fields */}
            <Typography level="title-sm" sx={{ mt: 1 }}>
              Pricing (at least one required)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <FormControl>
                <FormLabel>Replace Price</FormLabel>
                <Input
                  type="text"
                  value={formData.replace_price}
                  onChange={(e) => setFormData({ ...formData, replace_price: formatPrice(e.target.value) })}
                  placeholder="0.00"
                  startDecorator="$"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Remove Price</FormLabel>
                <Input
                  type="text"
                  value={formData.remove_price}
                  onChange={(e) => setFormData({ ...formData, remove_price: formatPrice(e.target.value) })}
                  placeholder="0.00"
                  startDecorator="$"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Detach & Reset Price</FormLabel>
                <Input
                  type="text"
                  value={formData.detach_reset_price}
                  onChange={(e) => setFormData({ ...formData, detach_reset_price: formatPrice(e.target.value) })}
                  placeholder="0.00"
                  startDecorator="$"
                />
              </FormControl>
            </Box>

            <FormControl>
              <FormLabel>Supporting Information</FormLabel>
              <Textarea
                value={formData.supporting_info}
                onChange={(e) => setFormData({ ...formData, supporting_info: e.target.value })}
                placeholder="Add any additional notes or information about this item..."
                minRows={3}
                maxRows={6}
              />
            </FormControl>

            <FormControl>
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                label="Active"
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}